import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { practitionerService } from '../../services/practitionerService';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const CredentialManagementEmailSystem = () => {
  const navigate = useNavigate();
  const { userProfile, loading: authLoading, signOut } = useAuth();
  
  const [activeTab, setActiveTab] = useState('credentials');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Credential generation state
  const [practitioners, setPractitioners] = useState([]);
  const [selectedPractitioner, setSelectedPractitioner] = useState('');
  const [tempPassword, setTempPassword] = useState('');
  const [sendingCredentials, setSendingCredentials] = useState(false);
  
  // Password reset state
  const [resetEmail, setResetEmail] = useState('');
  const [sendingReset, setSendingReset] = useState(false);
  
  // Email delivery logs
  const [deliveryLogs, setDeliveryLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  // Check super admin access
  useEffect(() => {
    if (!authLoading && userProfile?.role !== 'super_admin') {
      navigate('/admin-login', { replace: true });
    }
  }, [authLoading, userProfile, navigate]);

  // Load practitioners
  useEffect(() => {
    if (!authLoading && userProfile?.role === 'super_admin') {
      loadPractitioners();
      loadDeliveryLogs();
    }
  }, [authLoading, userProfile]);

  const loadPractitioners = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase?.from('practitioners')?.select('id, slug, practitioner_name, practitioner_title, email')?.eq('status', 'active')?.order('practitioner_name');
      
      if (error) throw error;
      setPractitioners(data || []);
    } catch (err) {
      console.error('Error loading practitioners:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadDeliveryLogs = async () => {
    try {
      setLogsLoading(true);
      // Mock delivery logs - in production, fetch from audit_logs or email tracking table
      const { data, error } = await supabase?.from('audit_logs')?.select('*')?.eq('action', 'SEND_CREDENTIALS_EMAIL')?.order('created_at', { ascending: false })?.limit(20);
      
      if (error) throw error;
      setDeliveryLogs(data || []);
    } catch (err) {
      console.error('Error loading delivery logs:', err);
    } finally {
      setLogsLoading(false);
    }
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars?.charAt(Math.floor(Math.random() * chars?.length));
    }
    setTempPassword(password);
  };

  const handleSendCredentials = async (e) => {
    e?.preventDefault();
    setSendingCredentials(true);
    setError('');
    setSuccess('');

    try {
      if (!selectedPractitioner || !tempPassword) {
        throw new Error('Veuillez sélectionner un praticien et générer un mot de passe');
      }

      const practitioner = practitioners?.find(p => p?.id === selectedPractitioner);
      if (!practitioner) {
        throw new Error('Praticien introuvable');
      }

      // Get user profile for this practitioner
      const { data: userProfile } = await supabase?.from('user_profiles')?.select('email')?.eq('practitioner_id', practitioner?.id)?.single();

      if (!userProfile) {
        throw new Error('Aucun compte utilisateur trouvé pour ce praticien');
      }

      // Send credentials email via edge function
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session) {
        throw new Error('Non authentifié');
      }

      const response = await fetch(
        `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-credentials-email`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session?.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            practitioner_id: practitioner?.id,
            email: userProfile?.email,
            password: tempPassword,
            practitioner_name: `${practitioner?.practitioner_title} ${practitioner?.practitioner_name}`,
            public_url: practitionerService?.getPublicUrl(practitioner?.slug),
            dashboard_url: `${window.location?.origin}/individual-practitioner-dashboard`
          }),
        }
      );

      const result = await response?.json();

      if (!response?.ok) {
        throw new Error(result.error || 'Erreur lors de l\'envoi de l\'email');
      }

      setSuccess('✅ Email d\'identifiants envoyé avec succès');
      setSelectedPractitioner('');
      setTempPassword('');
      await loadDeliveryLogs();
    } catch (err) {
      console.error('Error sending credentials:', err);
      setError(err?.message || 'Erreur lors de l\'envoi');
    } finally {
      setSendingCredentials(false);
    }
  };

  const handleSendPasswordReset = async (e) => {
    e?.preventDefault();
    setSendingReset(true);
    setError('');
    setSuccess('');

    try {
      if (!resetEmail) {
        throw new Error('Veuillez entrer une adresse email');
      }

      // Send password reset email via edge function
      const response = await fetch(
        `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-reset-email`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: resetEmail
          }),
        }
      );

      const result = await response?.json();

      if (!response?.ok) {
        throw new Error(result.error || 'Erreur lors de l\'envoi de l\'email');
      }

      setSuccess('✅ Email de réinitialisation envoyé avec succès');
      setResetEmail('');
      await loadDeliveryLogs();
    } catch (err) {
      console.error('Error sending reset email:', err);
      setError(err?.message || 'Erreur lors de l\'envoi');
    } finally {
      setSendingReset(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/login', { replace: true });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'dd/MM/yyyy HH:mm', { locale: fr });
    } catch {
      return '-';
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#0F0F12] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent mx-auto mb-4"></div>
          <p className="text-secondary">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Gestion des Identifiants & Emails - EndoReferral</title>
      </Helmet>
      <div className="min-h-screen bg-[#0F0F12]">
        {/* Header */}
        <header className="sticky top-0 z-50 bg-card/95 backdrop-blur-xl border-b border-white/8 shadow-xl">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => navigate('/super-admin-dashboard')}
                  className="p-2 text-muted hover:text-primary hover:bg-card-hover rounded-lg transition-colors"
                >
                  <Icon name="ArrowLeft" size={20} />
                </button>
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center shadow-lg">
                  <Icon name="Mail" size={24} className="text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-primary">Gestion des Identifiants & Emails</h1>
                  <p className="text-sm text-muted">Envoi d'identifiants et réinitialisation de mots de passe</p>
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-3 px-4 py-2 bg-card-elevated rounded-xl">
                  <div className="w-8 h-8 bg-gradient-to-br from-accent to-primary rounded-lg flex items-center justify-center">
                    <Icon name="User" size={16} className="text-white" />
                  </div>
                  <div className="hidden sm:block">
                    <p className="text-sm font-medium text-primary">{userProfile?.fullName}</p>
                    <p className="text-xs text-muted">{userProfile?.email}</p>
                  </div>
                </div>
                <button
                  onClick={handleSignOut}
                  className="flex items-center gap-2 px-4 py-2 text-error hover:bg-error-bg rounded-xl transition-colors"
                >
                  <Icon name="LogOut" size={20} />
                  <span className="hidden sm:inline">Déconnexion</span>
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Tabs */}
          <div className="mb-6 flex gap-2 bg-card rounded-xl p-1 border border-white/8">
            <button
              onClick={() => setActiveTab('credentials')}
              className={`flex-1 px-4 py-3 rounded-lg font-medium transition-all ${
                activeTab === 'credentials' ?'bg-accent text-white shadow-lg' :'text-secondary hover:text-primary hover:bg-card-hover'
              }`}
            >
              <Icon name="Key" size={18} className="inline mr-2" />
              Nouveaux Identifiants
            </button>
            <button
              onClick={() => setActiveTab('reset')}
              className={`flex-1 px-4 py-3 rounded-lg font-medium transition-all ${
                activeTab === 'reset' ?'bg-accent text-white shadow-lg' :'text-secondary hover:text-primary hover:bg-card-hover'
              }`}
            >
              <Icon name="RefreshCw" size={18} className="inline mr-2" />
              Réinitialisation
            </button>
            <button
              onClick={() => setActiveTab('logs')}
              className={`flex-1 px-4 py-3 rounded-lg font-medium transition-all ${
                activeTab === 'logs' ?'bg-accent text-white shadow-lg' :'text-secondary hover:text-primary hover:bg-card-hover'
              }`}
            >
              <Icon name="Activity" size={18} className="inline mr-2" />
              Historique
            </button>
          </div>

          {/* Status Messages */}
          {error && (
            <div className="mb-6 bg-error-bg border border-error/30 rounded-xl p-4 flex items-center gap-3">
              <Icon name="AlertCircle" size={20} className="text-error" />
              <p className="text-error">{error}</p>
            </div>
          )}
          {success && (
            <div className="mb-6 bg-success-bg border border-success/30 rounded-xl p-4 flex items-center gap-3">
              <Icon name="CheckCircle" size={20} className="text-success" />
              <p className="text-success">{success}</p>
            </div>
          )}

          {/* Credentials Tab */}
          {activeTab === 'credentials' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Credential Generation Form */}
              <div className="bg-card rounded-2xl shadow-2xl border border-white/8 p-6">
                <h2 className="text-lg font-bold text-primary mb-6 flex items-center gap-2">
                  <Icon name="Key" size={20} className="text-accent" />
                  Générer de Nouveaux Identifiants
                </h2>
                <form onSubmit={handleSendCredentials} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-secondary mb-2">Sélectionner un praticien</label>
                    <Select
                      value={selectedPractitioner}
                      onChange={(e) => setSelectedPractitioner(e?.target?.value)}
                      required
                    >
                      <option value="">-- Choisir un praticien --</option>
                      {practitioners?.map((p) => (
                        <option key={p?.id} value={p?.id}>
                          {p?.practitioner_title} {p?.practitioner_name} ({p?.email})
                        </option>
                      ))}
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary mb-2">Mot de passe temporaire</label>
                    <div className="flex gap-2">
                      <Input
                        type="text"
                        value={tempPassword}
                        onChange={(e) => setTempPassword(e?.target?.value)}
                        placeholder="Générer un mot de passe"
                        required
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        onClick={generatePassword}
                        className="px-4 bg-accent hover:bg-accent/90 text-white"
                      >
                        <Icon name="RefreshCw" size={18} />
                      </Button>
                    </div>
                    <p className="text-xs text-muted mt-2">Min. 8 caractères recommandés</p>
                  </div>
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
                    <p className="text-sm text-blue-400 font-medium mb-2">📧 Email envoyé contiendra :</p>
                    <ul className="text-xs text-secondary space-y-1">
                      <li>• Identifiants de connexion</li>
                      <li>• Lien vers le tableau de bord</li>
                      <li>• Lien public d'adressage</li>
                      <li>• Instructions de sécurité</li>
                    </ul>
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-accent hover:bg-accent/90 text-white py-3"
                    disabled={sendingCredentials || loading}
                  >
                    {sendingCredentials ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white inline-block mr-2"></div>
                        Envoi en cours...
                      </>
                    ) : (
                      <>
                        <Icon name="Send" size={18} className="inline mr-2" />
                        Envoyer les Identifiants
                      </>
                    )}
                  </Button>
                </form>
              </div>

              {/* Info Panel */}
              <div className="space-y-6">
                <div className="bg-card rounded-2xl shadow-2xl border border-white/8 p-6">
                  <h3 className="text-lg font-bold text-primary mb-4 flex items-center gap-2">
                    <Icon name="Info" size={20} className="text-accent" />
                    Politique de Sécurité
                  </h3>
                  <div className="space-y-3 text-sm text-secondary">
                    <div className="flex items-start gap-3">
                      <Icon name="CheckCircle" size={16} className="text-success mt-0.5" />
                      <p>Mots de passe temporaires générés aléatoirement</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <Icon name="CheckCircle" size={16} className="text-success mt-0.5" />
                      <p>Emails envoyés via Resend avec chiffrement TLS</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <Icon name="CheckCircle" size={16} className="text-success mt-0.5" />
                      <p>Recommandation de changement au premier login</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <Icon name="CheckCircle" size={16} className="text-success mt-0.5" />
                      <p>Audit complet de tous les envois d'emails</p>
                    </div>
                  </div>
                </div>

                <div className="bg-card rounded-2xl shadow-2xl border border-white/8 p-6">
                  <h3 className="text-lg font-bold text-primary mb-4 flex items-center gap-2">
                    <Icon name="Zap" size={20} className="text-accent" />
                    Statistiques Rapides
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-card-elevated rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-accent">{practitioners?.length}</p>
                      <p className="text-xs text-muted mt-1">Praticiens actifs</p>
                    </div>
                    <div className="bg-card-elevated rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-success">{deliveryLogs?.length}</p>
                      <p className="text-xs text-muted mt-1">Emails envoyés</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Password Reset Tab */}
          {activeTab === 'reset' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Reset Form */}
              <div className="bg-card rounded-2xl shadow-2xl border border-white/8 p-6">
                <h2 className="text-lg font-bold text-primary mb-6 flex items-center gap-2">
                  <Icon name="RefreshCw" size={20} className="text-accent" />
                  Réinitialisation de Mot de Passe
                </h2>
                <form onSubmit={handleSendPasswordReset} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-secondary mb-2">Adresse email</label>
                    <Input
                      type="email"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e?.target?.value)}
                      placeholder="utilisateur@example.com"
                      required
                    />
                    <p className="text-xs text-muted mt-2">Email du praticien ou super admin</p>
                  </div>
                  <div className="bg-warning-bg border border-warning/30 rounded-xl p-4">
                    <p className="text-sm text-warning font-medium mb-2">⚠️ Processus de réinitialisation</p>
                    <ul className="text-xs text-secondary space-y-1">
                      <li>• Lien de réinitialisation valide 1 heure</li>
                      <li>• Usage unique du lien</li>
                      <li>• Email envoyé instantanément</li>
                      <li>• Vérification de l'existence de l'email</li>
                    </ul>
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-accent hover:bg-accent/90 text-white py-3"
                    disabled={sendingReset}
                  >
                    {sendingReset ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white inline-block mr-2"></div>
                        Envoi en cours...
                      </>
                    ) : (
                      <>
                        <Icon name="Send" size={18} className="inline mr-2" />
                        Envoyer le Lien de Réinitialisation
                      </>
                    )}
                  </Button>
                </form>
              </div>

              {/* Info Panel */}
              <div className="bg-card rounded-2xl shadow-2xl border border-white/8 p-6">
                <h3 className="text-lg font-bold text-primary mb-4 flex items-center gap-2">
                  <Icon name="Shield" size={20} className="text-accent" />
                  Sécurité de la Réinitialisation
                </h3>
                <div className="space-y-4">
                  <div className="bg-card-elevated rounded-xl p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 bg-success/10 rounded-lg flex items-center justify-center">
                        <Icon name="Lock" size={16} className="text-success" />
                      </div>
                      <h4 className="text-sm font-semibold text-primary">Token Sécurisé</h4>
                    </div>
                    <p className="text-xs text-secondary">Génération de token unique avec hachage cryptographique</p>
                  </div>
                  <div className="bg-card-elevated rounded-xl p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 bg-blue-500/10 rounded-lg flex items-center justify-center">
                        <Icon name="Clock" size={16} className="text-blue-400" />
                      </div>
                      <h4 className="text-sm font-semibold text-primary">Expiration Automatique</h4>
                    </div>
                    <p className="text-xs text-secondary">Lien valide pendant 1 heure maximum</p>
                  </div>
                  <div className="bg-card-elevated rounded-xl p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 bg-purple-500/10 rounded-lg flex items-center justify-center">
                        <Icon name="Eye" size={16} className="text-purple-400" />
                      </div>
                      <h4 className="text-sm font-semibold text-primary">Audit Complet</h4>
                    </div>
                    <p className="text-xs text-secondary">Toutes les tentatives sont enregistrées dans les logs</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Delivery Logs Tab */}
          {activeTab === 'logs' && (
            <div className="bg-card rounded-2xl shadow-2xl border border-white/8 overflow-hidden">
              <div className="p-6 border-b border-white/8">
                <h2 className="text-lg font-bold text-primary flex items-center gap-2">
                  <Icon name="Activity" size={20} className="text-accent" />
                  Historique des Envois d'Emails
                </h2>
              </div>
              {logsLoading ? (
                <div className="p-12 text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent mx-auto mb-4"></div>
                  <p className="text-secondary">Chargement...</p>
                </div>
              ) : deliveryLogs?.length === 0 ? (
                <div className="p-12 text-center">
                  <Icon name="Inbox" size={48} className="text-muted mx-auto mb-4" />
                  <p className="text-muted">Aucun email envoyé récemment</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-card-elevated border-b border-white/8">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-muted uppercase tracking-wider">Date</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-muted uppercase tracking-wider">Action</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-muted uppercase tracking-wider">Destinataire</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-muted uppercase tracking-wider">Statut</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/8">
                      {deliveryLogs?.map((log) => (
                        <tr key={log?.id} className="hover:bg-card-hover transition-colors">
                          <td className="px-6 py-4 text-sm text-secondary">{formatDate(log?.created_at)}</td>
                          <td className="px-6 py-4">
                            <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/30 rounded-full text-xs font-medium">
                              {log?.action}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-secondary">{log?.user_email || '-'}</td>
                          <td className="px-6 py-4">
                            <span className="px-3 py-1 bg-success-bg text-success border border-success/30 rounded-full text-xs font-medium">
                              Envoyé
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </main>
      </div>
    </>
  );
};

export default CredentialManagementEmailSystem;